import { useState } from "react";
import { Gavel } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loadable, Tabbar, TabPanel, useTabs, useLoad, fmt, Status, ActionDialog, Section, History } from "@/components/control/kit";
import { V3Page, V3Aside, Field, Pill, V3Actions, FilterEmpty } from "@/components/control/v3";

function AppealView({ a, assignees, reload, onBack }) {
  const { user } = useAuth();
  const [dialog, setDialog] = useState(null);
  const mine = a.assignee?.id === user?.id;
  const options = assignees.filter((x) => x.name !== a.original_decider).map((x) => ({ value: x.id, label: x.name }));
  const next = a.status === "offen" ? "Andere berechtigte Zuständigkeit zuweisen"
    : a.status === "zugewiesen" ? `Zugewiesen an ${a.assignee?.name}` : a.outcome;
  return (
    <div className="space-y-2.5" data-testid={`appeal-${a.id}`}>
      <div>{a.status === "entschieden" ? <Pill tone="success" testId="appeal-pill">Entschieden</Pill> : <Pill testId="appeal-pill">Vor der Entscheidung</Pill>}</div>
      <Field label="Fall" testId="appeal-case">{a.case}</Field>
      <Field label="Einwand" testId="appeal-text">{a.text}</Field>
      <Field label="Bisherige Massnahme" testId="appeal-measure">{a.measure}{a.original_decider ? ` · entschieden von ${a.original_decider}` : ""}</Field>
      <Field label="Erneute Prüfung" testId="appeal-next">{next}</Field>
      {a.status !== "entschieden" && (
        <V3Actions testId="appeal" secondary="Abbrechen" onSecondary={onBack}
          primary={a.status === "offen" || !mine ? "Erneute Prüfung zuweisen" : "Entscheidung treffen"}
          onPrimary={() => setDialog(a.status === "zugewiesen" && mine ? "decide" : "assign")} />
      )}
      <ActionDialog open={dialog === "assign"} onOpenChange={(o) => setDialog(o ? "assign" : null)} title="Erneute Prüfung zuweisen" testId="appeal-assign-dialog"
        description="Die erneute Prüfung übernimmt eine andere berechtigte Person als die ursprünglich entscheidende." submitLabel="Zuweisen" onDone={reload}
        fields={[{ name: "assignee_id", label: "Zuständige Person", type: "select", required: true, options },
          { name: "reason", label: "Begründung", type: "textarea", required: true }]}
        onSubmit={async (v) => { await api.post(`/appeals/${a.id}/assign`, v); return "Erneute Prüfung zugewiesen."; }} />
      <ActionDialog open={dialog === "decide"} onOpenChange={(o) => setDialog(o ? "decide" : null)} title="Über den Einspruch entscheiden" testId="appeal-decide-dialog"
        submitLabel="Entscheidung speichern" onDone={reload}
        fields={[{ name: "decision", label: "Entscheidung", type: "select", required: true,
          options: [{ value: "bestätigt", label: "Massnahme bestätigen" }, { value: "aufgehoben", label: "Massnahme aufheben" }] },
          { name: "reason", label: "Begründung", type: "textarea", required: true },
          { name: "message", label: "Nachricht an das Mitglied", type: "textarea" }]}
        onSubmit={async (v) => { await api.post(`/appeals/${a.id}/decide`, v); return "Entscheidung gespeichert."; }} />
      {a.history.length > 0 && <Section title="Verlauf" className="mt-4" testId="appeal-history"><History items={a.history} /></Section>}
    </div>
  );
}

function AppealList({ items, onSelect }) {
  return (
    <ul className="space-y-2.5" data-testid="appeals-list">
      {items.map((a) => (
        <li key={a.id}>
          <button type="button" onClick={() => onSelect(a.id)} data-testid={`appeal-open-${a.id}`}
            className="flex w-full flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E6E1F0] bg-white px-4 py-3 text-left transition-colors hover:border-brand-magenta">
            <span><span className="block font-semibold text-brand-purple">{a.case}</span><span className="text-sm text-zinc-600">{a.measure} · {fmt(a.created_at)}</span></span>
            <Status value={a.status} />
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function Appeals() {
  const [status, setStatus] = useState("open");
  const [selected, setSelected] = useState(null);
  const tabs = useTabs([{ value: "open", label: "Offen" }, { value: "entschieden", label: "Entschieden" }, { value: "all", label: "Alle" }], status);
  const st = useLoad(`/appeals?status=${status}`);
  const current = st.data?.appeals.find((a) => a.id === selected);
  return (
    <V3Page crumb="Meldungen & Sicherheit" title="Einspruch gegen Moderationsentscheidung" testId="appeals-page"
      subtitle="Eine erneute Prüfung erhält den Einwand und nur den notwendigen Kontext."
      aside={<V3Aside subjectTitle="Ausgewählter Vorgang" subject={current?.case || "Kein Einspruch ausgewählt"} />}>
      {!current && <Tabbar tabs={tabs} onChange={(v) => { setStatus(v); setSelected(null); }} testId="appeals-tabs" />}
      <TabPanel tabs={tabs} testId="appeals-panel">
        <Loadable state={st}>
          {(d) => (current ? <AppealView a={current} assignees={d.assignees} reload={st.reload} onBack={() => setSelected(null)} />
            : d.appeals.length === 0 ? <FilterEmpty filters={[["Status", tabs.items[tabs.index].label], ["Bereich", "Einsprüche"]]}
              onReset={status !== "all" ? () => setStatus("all") : null} />
              : <AppealList items={d.appeals} onSelect={setSelected} />)}
        </Loadable>
      </TabPanel>
      <p className="flex items-center gap-2 pt-2 text-xs text-zinc-600"><Gavel className="h-4 w-4" aria-hidden="true" />Einsprüche reichen Mitglieder in der Mitglieder-App zu einer sie betreffenden Massnahme ein.</p>
    </V3Page>
  );
}
